import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Space,
  Badge,
  Timeline,
  Steps,
  Avatar
} from 'antd';
import {
  RocketOutlined,
  SafetyOutlined,
  TeamOutlined,
  CalendarOutlined,
  BarChartOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  PlayCircleOutlined,
  StarOutlined,
  HeartOutlined,
  ClockCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import styles from './Home.module.css';
import MobileAppPromo from '../components/MobileAppPromo';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const Home = () => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setLoading(false), 800);
    
    // Auto-advance steps for demo
    const timer = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % 4);
    }, 3000);
    
    return () => clearInterval(timer);
  }, []);

  const userName = keycloak?.tokenParsed?.preferred_username || t('home.guest');
  const isAuthenticated = keycloak?.authenticated;

  const features = [
    {
      icon: <TeamOutlined className={styles.featureIcon} />,
      title: t('home.feature1Title'),
      description: t('home.feature1Description'),
      color: '#1890ff',
      path: '/employees'
    },
    {
      icon: <CalendarOutlined className={styles.featureIcon} />,
      title: t('home.feature2Title'),
      description: t('home.feature2Description'),
      color: '#52c41a',
      path: '/week-plan'
    },
    {
      icon: <BarChartOutlined className={styles.featureIcon} />,
      title: t('home.feature3Title'),
      description: t('home.feature3Description'),
      color: '#722ed1',
      path: '/summary'
    },
    {
      icon: <SettingOutlined className={styles.featureIcon} />,
      title: t('home.feature4Title'),
      description: t('home.feature4Description'),
      color: '#fa8c16',
      path: '/settings/week-template'
    }
  ];

  const adminFeatures = [
    {
      icon: <TeamOutlined />,
      title: t('welcomepage.admin1'),
      completed: true
    },
    {
      icon: <CalendarOutlined />,
      title: t('welcomepage.admin2'),
      completed: true
    },
    {
      icon: <BarChartOutlined />,
      title: t('welcomepage.admin3'),
      completed: false
    },
    {
      icon: <SettingOutlined />,
      title: t('welcomepage.admin4'),
      completed: false
    }
  ];

  const employeeFeatures = [
    {
      icon: <ClockCircleOutlined />,
      title: t('welcomepage.employee1'),
      completed: true
    },
    {
      icon: <CalendarOutlined />,
      title: t('welcomepage.employee2'),
      completed: true
    },
    {
      icon: <UserOutlined />,
      title: t('welcomepage.employee3'),
      completed: false
    },
    {
      icon: <BarChartOutlined />,
      title: t('welcomepage.employee4'),
      completed: false
    }
  ];

  const quickStartSteps = [
    {
      title: t('home.step1Title'),
      description: t('home.step1Description'),
      icon: <UserOutlined />
    },
    {
      title: t('home.step2Title'),
      description: t('home.step2Description'),
      icon: <TeamOutlined />
    },
    {
      title: t('home.step3Title'),
      description: t('home.step3Description'),
      icon: <CalendarOutlined />
    },
    {
      title: t('home.step4Title'),
      description: t('home.step4Description'),
      icon: <RocketOutlined />
    }
  ];

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/registration');
    }
  };

  const handleFeatureClick = (path) => {
    if (isAuthenticated) {
      navigate(path);
    } else {
      navigate('/registration');
    }
  };

  return (
    <div className={styles.home}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <Badge.Ribbon text={t('home.latest')} color="blue" className={styles.latestBadge}>
              <Title level={1} className={styles.heroTitle}>
                {t('welcomepage.welcomeTitle')}
              </Title>
            </Badge.Ribbon>
            <Title level={2} className={styles.heroSubtitle}>
              {t('welcomepage.welcomeSubtitle')}
            </Title>
            <Paragraph className={styles.heroDescription}>
              {t('welcomepage.intro1')}
            </Paragraph>
            <div className={styles.heroStats}>
              <div className={styles.statItem}>
                <HeartOutlined className={styles.statIcon} />
                <div>
                  <Text strong className={styles.statNumber}>99.9%</Text>
                  <Text className={styles.statLabel}>{t('home.uptime')}</Text>
                </div>
              </div>
            {/*   <div className={styles.statItem}>
                <TrophyOutlined className={styles.statIcon} />
                <div>
                  <Text strong className={styles.statNumber}>1000+</Text>
                  <Text className={styles.statLabel}>{t('home.users')}</Text>
                </div>
              </div> */}
              <div className={styles.statItem}>
                <StarOutlined className={styles.statIcon} />
                <div>
                  <Text strong className={styles.statNumber}>4.9/5</Text>
                  <Text className={styles.statLabel}>{t('home.rating')}</Text>
                </div>
              </div>
            </div>
            <Space size="large" className={styles.heroActions}>
              <Button 
                type="primary" 
                size="large" 
                icon={<PlayCircleOutlined />}
                className={styles.primaryButton}
                onClick={handleGetStarted}
                loading={loading}
              >
                {isAuthenticated ? t('home.goToDashboard') : t('home.getStarted')}
              </Button>
              <Button 
                size="large" 
                icon={<ArrowRightOutlined />}
                className={styles.secondaryButton}
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              >
                {t('home.learnMore')}
              </Button>
            </Space>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.welcomeCard}>
              <Avatar size={80} icon={<UserOutlined />} className={styles.welcomeAvatar} />
              <Title level={4} className={styles.welcomeCardTitle}>
                {isAuthenticated ? `${t('home.welcomeBack')}, ${userName}!` : t('welcomepage.welcomeSubtitle')}
              </Title>
              {isAuthenticated && (
                <Button
                  type="primary"
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate('/dashboard')}
                  className={styles.welcomeButton}
                >
                  {t('home.goToDashboard')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Get the mobile app (iOS + Android QR codes) */}
      <MobileAppPromo />

      {/* Features Section */}
      <div id="features" className={styles.featuresSection}>
        <div className={styles.sectionHeader}>
          <Title level={2} className={styles.sectionTitle}>
            {t('home.featuresTitle')}
          </Title>
          <Paragraph className={styles.sectionDescription}>
            {t('home.featuresDescription')}
          </Paragraph>
        </div>
        
        <Row gutter={[24, 24]} className={styles.featuresGrid}>
          {features.map((feature, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card 
                className={styles.featureCard}
                hoverable
                onClick={() => handleFeatureClick(feature.path)}
                cover={
                  <div className={styles.featureCardCover} style={{ background: `linear-gradient(135deg, ${feature.color}15 0%, ${feature.color}25 100%)` }}>
                    <div className={styles.featureIconWrapper} style={{ color: feature.color }}>
                      {feature.icon}
                    </div>
                  </div>
                }
              >
                <Card.Meta
                  title={<Text className={styles.featureTitle}>{feature.title}</Text>}
                  description={<Text className={styles.featureDescription}>{feature.description}</Text>}
                />
                <Button 
                  type="text" 
                  icon={<ArrowRightOutlined />} 
                  className={styles.featureButton}
                  style={{ color: feature.color }}
                >
                  {t('home.explore')}
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Admin & Employee Features */}
      <Row gutter={[48, 48]} className={styles.rolesSection}>
        <Col xs={24} lg={12}>
          <Card className={styles.roleCard}>
            <div className={styles.roleHeader}>
              <div className={styles.roleIcon} style={{ background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)' }}>
                🔧
              </div>
              <Title level={3} className={styles.roleTitle}>
                {t('welcomepage.adminTitle')}
              </Title>
            </div>
            <Timeline className={styles.roleTimeline}>
              {adminFeatures.map((item, index) => (
                <Timeline.Item
                  key={index}
                  dot={item.completed ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : item.icon}
                  color={item.completed ? 'green' : 'blue'}
                >
                  <div className={styles.timelineItem}>
                    <Text className={item.completed ? styles.completedFeature : styles.pendingFeature}>
                      {item.title}
                    </Text>
                    {item.completed && <Badge status="success" text={t('home.available')} />}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card className={styles.roleCard}>
            <div className={styles.roleHeader}>
              <div className={styles.roleIcon} style={{ background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)' }}>
                📆
              </div>
              <Title level={3} className={styles.roleTitle}>
                {t('welcomepage.employeeTitle')}
              </Title>
            </div>
            <Timeline className={styles.roleTimeline}>
              {employeeFeatures.map((item, index) => (
                <Timeline.Item
                  key={index}
                  dot={item.completed ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : item.icon}
                  color={item.completed ? 'green' : 'blue'}
                >
                  <div className={styles.timelineItem}>
                    <Text className={item.completed ? styles.completedFeature : styles.pendingFeature}>
                      {item.title}
                    </Text>
                    {item.completed && <Badge status="success" text={t('home.available')} />}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
      </Row>

      {/* Quick Start Guide */}
      <div className={styles.quickStartSection}>
        <div className={styles.sectionHeader}>
          <Title level={2} className={styles.sectionTitle}>
            {t('home.quickStartTitle')}
          </Title>
          <Paragraph className={styles.sectionDescription}>
            {t('home.quickStartDescription')}
          </Paragraph>
        </div>
        
        <Card className={styles.quickStartCard}>
          <Steps 
            current={currentStep} 
            className={styles.quickStartSteps}
            responsive={false}
          >
            {quickStartSteps.map((step, index) => (
              <Step
                key={index}
                title={step.title}
                description={step.description}
                icon={step.icon}
              />
            ))}
          </Steps>
          
          <div className={styles.quickStartActions}>
            <Button 
              type="primary" 
              size="large" 
              icon={<RocketOutlined />}
              onClick={handleGetStarted}
              className={styles.startButton}
            >
              {t('home.startNow')}
            </Button>
          </div>
        </Card>
      </div>

      {/* Call to Action */}
      <div className={styles.ctaSection}>
        <Card className={styles.ctaCard}>
          <div className={styles.ctaContent}>
            <SafetyOutlined className={styles.ctaIcon} />
            <Title level={3} className={styles.ctaTitle}>
              {t('welcomepage.closing1')}
            </Title>
            <Paragraph className={styles.ctaDescription}>
              {t('welcomepage.closing2')}
            </Paragraph>
            <Button 
              type="primary" 
              size="large" 
              icon={<ArrowRightOutlined />}
              onClick={handleGetStarted}
              className={styles.ctaButton}
            >
              {isAuthenticated ? t('home.continueToDashboard') : t('home.joinNow')}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Home;